import { useCallback, useMemo } from 'react';

import { Form } from 'antd';
import type { FormInstance } from 'antd';
import type { NamePath } from 'antd/es/form/interface';

import type { ConnectorField, UpdateConnectorRequest } from '@/entities/connector';

const DATA_INFO_FIELDS: readonly ConnectorField[] = [
  {
    label: 'enterprise',
    inputType: 'text',
    required: true,
    placeholder: '입력해 주세요',
    validationRules: { pattern: '^[a-z0-9_-]+$' },
  },
  {
    label: 'site',
    inputType: 'text',
    required: true,
    placeholder: '입력해 주세요',
    validationRules: { pattern: '^[a-z0-9_-]+$' },
  },
  {
    label: 'area',
    inputType: 'text',
    required: true,
    placeholder: '입력해 주세요',
    validationRules: { pattern: '^[a-z0-9_-]+$' },
  },
  {
    label: 'process',
    inputType: 'text',
    required: false,
    placeholder: '입력해 주세요',
    validationRules: { pattern: '^[a-z0-9_-]+$' },
  },
  {
    label: 'unit',
    inputType: 'text',
    required: false,
    placeholder: '입력해 주세요',
    validationRules: { pattern: '^[a-z0-9_-]+$' },
  },
  {
    label: 'component',
    inputType: 'text',
    required: false,
    placeholder: '입력해 주세요',
    validationRules: { pattern: '^[a-z0-9_-]+$' },
  },
];

const DATA_NAME_FIELDS: readonly ConnectorField[] = [
  {
    label: 'dataName',
    inputType: 'text',
    required: true,
    placeholder: 'Data name을 입력해 주세요',
    validationRules: { pattern: '^[a-z0-9_-]+$' },
  },
];

export const ASSET_URI_FIELDS = {
  dataInfo: DATA_INFO_FIELDS,
  dataName: DATA_NAME_FIELDS,
} as const;

const ALL_FIELDS = [...DATA_INFO_FIELDS, ...DATA_NAME_FIELDS];
const RESET_FIELD_PATHS: NamePath[] = ALL_FIELDS.map((field) => ['dataInfo', field.label]);
const RESET_FIELD_LABELS = ALL_FIELDS.map((field) => field.label);
const REQUIRED_FIELD_LABELS = ALL_FIELDS.filter((field) => field.required).map((field) => field.label);

export const ASSET_URI_FIELD_PATHS: NamePath[] = RESET_FIELD_PATHS;

const getResetFieldSnapshot = (config: unknown) => {
  const normalizedConfig = (config ?? {}) as Record<string, string>;

  return RESET_FIELD_LABELS.reduce<Record<string, string>>((acc, label) => {
    acc[label] = normalizedConfig[label];
    return acc;
  }, {});
};

export const useAssetURIFieldsReset = (form: FormInstance<UpdateConnectorRequest>) => {
  const watchedConfig = Form.useWatch('dataInfo', form);

  const hasRequiredFields = REQUIRED_FIELD_LABELS.every(
    (label) => !!watchedConfig?.[label as keyof typeof watchedConfig]
  );
  const currentResetSnapshot = useMemo(() => getResetFieldSnapshot(watchedConfig), [watchedConfig]);

  const isReset = RESET_FIELD_LABELS.some((label) => !!currentResetSnapshot[label]);

  const handleResetFields = useCallback(() => {
    form.setFields(
      RESET_FIELD_PATHS.map((name) => ({
        name,
        value: undefined,
        errors: [],
        warnings: [],
      }))
    );
  }, [form]);

  return { isReset, hasRequiredFields, handleResetFields };
};
